// =============================================================================
// UiAlertProvider — Sistema central de notificaciones UI alineado con el
// design system (no más window.alert / window.confirm / window.prompt feos).
//
// Provee 3 primitivas vía hook `useUiAlert()`:
//
//   const { toast, confirm, prompt } = useUiAlert();
//
//   toast({ level: 'success', title: '...', description: '...' });
//   const ok = await confirm({ title: '¿Eliminar?', danger: true });
//   const txt = await prompt({ title: 'Nuevo nombre', defaultValue: 'X' });
//
// Además, escucha `onApiCall` y muestra un toast de error automáticamente
// cuando el backend devuelve 5xx o no responde (status 0). El LoggingProvider
// ya registra estos eventos en el módulo Depuración.
// =============================================================================
import {
  createContext, useContext, useState, useCallback, useEffect, useRef,
  type ReactNode, type CSSProperties,
} from 'react';
import {
  CheckCircle2, AlertTriangle, AlertCircle, Info, X, ArrowRight,
} from 'lucide-react';
import { onApiCall } from '../../api/client';
// Este provider vive FUERA del RegionalProvider (ver App.tsx), así que useTextos
// acá vería siempre el contexto por defecto (español). Se usa textoDe, que lee
// el idioma actual a nivel de módulo en el momento de pintar / disparar el toast.
import { textoDe } from '../../i18n/textos';
import { textosComun } from '../../i18n/modulos/comun';

// ============================================================================
// Tipos
// ============================================================================
export type ToastLevel = 'success' | 'info' | 'warn' | 'error';

interface ToastSpec {
  id: number;
  level: ToastLevel;
  title: string;
  description?: string;
  durationMs?: number;
}

interface ConfirmSpec {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Si true, el botón confirm se pinta rojo (acción destructiva). */
  danger?: boolean;
}

interface PromptSpec {
  title: string;
  message?: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Validación opcional. Devuelve null si OK, o mensaje de error. */
  validate?: (value: string) => string | null;
}

interface UiAlertContextValue {
  toast: (spec: Omit<ToastSpec, 'id'>) => void;
  confirm: (spec: ConfirmSpec) => Promise<boolean>;
  prompt: (spec: PromptSpec) => Promise<string | null>;
}

const UiAlertContext = createContext<UiAlertContextValue | undefined>(undefined);

// ============================================================================
// Iconos + colores por nivel
// ============================================================================
const LEVEL_META: Record<ToastLevel, { icon: typeof CheckCircle2; bg: string; ink: string; ring: string }> = {
  success: { icon: CheckCircle2,   bg: 'var(--ok-bg)',     ink: 'var(--ok-ink)',     ring: 'var(--ok)' },
  info:    { icon: Info,           bg: 'var(--blue-50, #e8f0fb)', ink: 'var(--blue-700, #084a8c)', ring: 'var(--blue-600)' },
  warn:    { icon: AlertTriangle,  bg: 'var(--warn-bg)',   ink: 'var(--warn-ink)',   ring: 'var(--warn)' },
  error:   { icon: AlertCircle,    bg: 'var(--danger-bg)', ink: 'var(--danger-ink)', ring: 'var(--danger)' },
};

// ============================================================================
// Provider
// ============================================================================
export function UiAlertProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastSpec[]>([]);
  const [confirmState, setConfirmState] = useState<{ spec: ConfirmSpec; resolve: (v: boolean) => void } | null>(null);
  const [promptState, setPromptState] = useState<{ spec: PromptSpec; resolve: (v: string | null) => void } | null>(null);

  const toastSeq = useRef(1);

  const toast = useCallback((spec: Omit<ToastSpec, 'id'>) => {
    const id = toastSeq.current++;
    const duration = spec.durationMs ?? (spec.level === 'error' ? 6000 : 4000);
    setToasts(prev => [...prev, { ...spec, id }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const confirm = useCallback((spec: ConfirmSpec): Promise<boolean> => {
    return new Promise(resolve => setConfirmState({ spec, resolve }));
  }, []);

  const prompt = useCallback((spec: PromptSpec): Promise<string | null> => {
    return new Promise(resolve => setPromptState({ spec, resolve }));
  }, []);

  // -------------------------------------------------------------------------
  // Listener global: API errors automáticos
  // -------------------------------------------------------------------------
  useEffect(() => {
    // Sin red (modo contingencia, oct-2026): al arrancar sin servidor, cada
    // pantalla lanza 5 a 10 consultas y salía un aviso por cada una, tapando
    // la barra de arriba. El indicador de conexión ya dice que no hay
    // servidor; acá queda UN aviso cada 30 s como máximo.
    let ultimoSinConexion = 0;
    const unsub = onApiCall((e) => {
      // Solo notificar errores severos. 4xx pueden ser validación esperada
      // (ej. "ya existe") y los maneja el caller con su propio toast.
      //
      // El 401 sí se avisa acá. Antes decía "401 ya tiene su propio flujo de
      // redirect a login" — ese flujo NO existía, y por creer que existía nadie
      // lo construyó. El resultado era que la sesión se vencía en silencio y el
      // usuario lo descubría al hacer clic en cualquier cosa y recibir "Token
      // faltante". Ahora client.ts avisa a UserProvider (vuelve al login) y acá
      // se dice por qué.
      if (e.sesionExpirada) {
        toast({
          level: 'warn',
          title: textoDe(textosComun, 'sesionExpiradaTitulo'),
          description: textoDe(textosComun, 'sesionExpiradaDesc'),
          durationMs: 8000,
        });
      } else if (e.status === 0) {
        const ahora = Date.now();
        if (ahora - ultimoSinConexion < 30_000) return;
        ultimoSinConexion = ahora;
        toast({
          level: 'error',
          title: textoDe(textosComun, 'sinConexionServidor'),
          description: `${e.method} ${e.path}${e.errorMessage ? ' · ' + e.errorMessage : ''}`,
          durationMs: 8000,
        });
      } else if (e.status >= 500) {
        toast({
          level: 'error',
          title: textoDe(textosComun, 'errorDelServidor', { status: e.status }),
          description: `${e.method} ${e.path} · ${e.errorMessage || textoDe(textosComun, 'falloInterno')}`,
          durationMs: 8000,
        });
      }
    });
    return unsub;
  }, [toast]);

  return (
    <UiAlertContext.Provider value={{ toast, confirm, prompt }}>
      {children}

      {/* ============================================================
          Zona de toasts (esquina inferior derecha, stacked)
          ============================================================ */}
      <div
        role="region"
        aria-live="polite"
        aria-label={textoDe(textosComun, 'notificaciones')}
        data-testid="toast-region"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          maxWidth: 'min(420px, calc(100vw - 32px))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <ToastCard
            key={t.id}
            spec={t}
            onDismiss={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
          />
        ))}
      </div>

      {/* ============================================================
          Confirm modal
          ============================================================ */}
      {confirmState && (
        <ConfirmDialog
          spec={confirmState.spec}
          onResult={(ok) => {
            const { resolve } = confirmState;
            setConfirmState(null);
            resolve(ok);
          }}
        />
      )}

      {/* ============================================================
          Prompt modal
          ============================================================ */}
      {promptState && (
        <PromptDialog
          spec={promptState.spec}
          onResult={(value) => {
            const { resolve } = promptState;
            setPromptState(null);
            resolve(value);
          }}
        />
      )}
    </UiAlertContext.Provider>
  );
}

export function useUiAlert(): UiAlertContextValue {
  const ctx = useContext(UiAlertContext);
  if (!ctx) throw new Error('useUiAlert debe usarse dentro de <UiAlertProvider>');
  return ctx;
}

// ============================================================================
// ToastCard
// ============================================================================
function ToastCard({ spec, onDismiss }: { spec: ToastSpec; onDismiss: () => void }) {
  const meta = LEVEL_META[spec.level];
  const Icon = meta.icon;
  return (
    <div
      className="animate-apple-slide-up"
      role="status"
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--line)',
        borderLeft: `4px solid ${meta.ring}`,
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-lg, 0 12px 32px rgba(15,23,42,.16))',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        pointerEvents: 'auto',
      }}
    >
      <span
        style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: meta.bg, color: meta.ink,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.3 }}>
          {spec.title}
        </div>
        {spec.description && (
          <div
            style={{
              fontSize: 13, fontWeight: 600, color: 'var(--muted-ink)',
              marginTop: 4, lineHeight: 1.4,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            }}
          >
            {spec.description}
          </div>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label={textoDe(textosComun, 'cerrar')}
        style={{
          width: 28, height: 28, borderRadius: 8,
          border: 0, background: 'transparent', color: 'var(--muted-ink)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0,
        }}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================================
// ConfirmDialog (modal centered)
// ============================================================================
function ConfirmDialog({ spec, onResult }: { spec: ConfirmSpec; onResult: (ok: boolean) => void }) {
  // Focus trap simple — esc cancela, enter confirma
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onResult(false); }
      if (e.key === 'Enter')  { e.preventDefault(); onResult(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onResult]);

  return (
    <ModalScrim onCancel={() => onResult(false)}>
      <div style={dialogStyle}>
        <h3 style={titleStyle}>{spec.title}</h3>
        {spec.message && (
          <div style={messageStyle}>{spec.message}</div>
        )}
        <div style={footerStyle}>
          <button
            onClick={() => onResult(false)}
            className="sk-btn sk-btn--ghost"
            style={{ minHeight: 44, padding: '10px 18px' }}
          >
            {spec.cancelLabel || textoDe(textosComun, 'cancelar')}
          </button>
          <button
            onClick={() => onResult(true)}
            className={spec.danger ? 'sk-btn' : 'sk-btn sk-btn--primary'}
            style={{
              minHeight: 44, padding: '10px 18px',
              ...(spec.danger ? {
                background: 'var(--danger)', color: '#fff',
                boxShadow: '0 8px 22px color-mix(in srgb, var(--danger) 30%, transparent)',
              } : {}),
            }}
          >
            {spec.confirmLabel || (spec.danger ? textoDe(textosComun, 'eliminar') : textoDe(textosComun, 'confirmar'))}
            {!spec.danger && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </ModalScrim>
  );
}

// ============================================================================
// PromptDialog (modal centered con input)
// ============================================================================
function PromptDialog({ spec, onResult }: { spec: PromptSpec; onResult: (v: string | null) => void }) {
  const [value, setValue] = useState(spec.defaultValue ?? '');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Autofocus + selección completa para reemplazo rápido
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 30);
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    if (spec.validate) {
      const err = spec.validate(trimmed);
      if (err) { setError(err); return; }
    }
    onResult(trimmed);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onResult(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onResult]);

  return (
    <ModalScrim onCancel={() => onResult(null)}>
      <div style={dialogStyle}>
        <h3 style={titleStyle}>{spec.title}</h3>
        {spec.message && (
          <div style={messageStyle}>{spec.message}</div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => { setValue(e.target.value); setError(null); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          placeholder={spec.placeholder}
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'var(--surface)',
            border: `1.5px solid ${error ? 'var(--danger)' : 'var(--line)'}`,
            borderRadius: 'var(--r-md)',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--ink)',
            outline: 'none',
            marginTop: 4,
          }}
        />
        {error && (
          <div style={{
            marginTop: 8, fontSize: 13, fontWeight: 600,
            color: 'var(--danger-ink)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <div style={footerStyle}>
          <button
            onClick={() => onResult(null)}
            className="sk-btn sk-btn--ghost"
            style={{ minHeight: 44, padding: '10px 18px' }}
          >
            {spec.cancelLabel || textoDe(textosComun, 'cancelar')}
          </button>
          <button
            onClick={submit}
            className="sk-btn sk-btn--primary"
            style={{ minHeight: 44, padding: '10px 18px' }}
          >
            {spec.confirmLabel || textoDe(textosComun, 'guardar')}
          </button>
        </div>
      </div>
    </ModalScrim>
  );
}

// ============================================================================
// Shared styles
// ============================================================================
function ModalScrim({ children, onCancel }: { children: ReactNode; onCancel: () => void }) {
  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(14,22,38,.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: 'uialert-fade .18s ease-out both',
      }}
    >
      <div onClick={e => e.stopPropagation()}>
        {children}
      </div>
      <style>{`@keyframes uialert-fade{from{opacity:0}to{opacity:1}}@keyframes uialert-pop{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

const dialogStyle: CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--r-lg)',
  boxShadow: '0 24px 48px rgba(15,23,42,.22)',
  padding: 24,
  width: 'min(440px, calc(100vw - 32px))',
  animation: 'uialert-pop .2s cubic-bezier(.16,.84,.44,1) both',
};
const titleStyle: CSSProperties = {
  fontSize: 19, fontWeight: 800, letterSpacing: '-.01em',
  color: 'var(--ink)', margin: 0, lineHeight: 1.25,
};
const messageStyle: CSSProperties = {
  fontSize: 14, fontWeight: 500, color: 'var(--muted-ink)',
  marginTop: 10, lineHeight: 1.5,
};
const footerStyle: CSSProperties = {
  display: 'flex', gap: 10, justifyContent: 'flex-end',
  marginTop: 20,
};
